import { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { QuotaData, getFamilyDisplayName } from './quotaUtils';

interface Props {
  quota: QuotaData;
  groupLabel?: string;
  groupFamilies?: string[];
  onClose: () => void;
  onSubmit: () => void;
}

const JUSTIFICATION_TEMPLATES = [
  {
    label: 'Production workload',
    text: 'Production workload: [description]. Architecture: [N] nodes x [G] GPUs. Duration: Permanent.',
  },
  {
    label: 'Research / training',
    text: 'Research training job requiring [N] GPUs for [duration]. Model: [name]. Expected completion: [date].',
  },
  {
    label: 'Scaling existing usage',
    text: 'Scaling existing workload from [current] to [target] instances to handle increased demand. Current utilization consistently above 80%.',
  },
  {
    label: 'New region deployment',
    text: 'Deploying to new region for latency/compliance requirements. Expected steady-state usage: [N] instances.',
  },
];

const PROVIDER_TIPS: Record<string, string[]> = {
  aws: [
    'Requests ≤2x current limit are usually auto-approved',
    'P-instance increases may require business justification review',
    'Request during US business hours for faster processing',
  ],
  gcp: [
    'Requests within 2x are typically auto-approved',
    'Include specific workload details for faster review',
    'New accounts may need billing history before large increases',
  ],
  azure: [
    'First-time GPU quota requests need Azure support approval — expect 1-3 business days',
    'Start small: request 8-24 vCPUs first, then increase incrementally after usage',
    'Requests >3x current limit are frequently rejected — use incremental steps',
    'Pay-as-you-go subscriptions have lower approval rates than EA/MCA agreements',
    'Include workload type, expected duration, and business justification',
    'NC-series (T4/A100/H100) approvals are easier than ND-series (NVLink multi-GPU)',
  ],
};

const PROVIDER_INFO: Record<string, { api: string; timeline: string }> = {
  aws: {
    api: 'AWS Service Quotas API',
    timeline: 'Typically approved within 24-48 hours. Large increases may require AWS review.',
  },
  gcp: {
    api: 'GCP Cloud Quotas API',
    timeline: 'Typically approved within 24-48 hours. Some requests may need Google review.',
  },
  azure: {
    api: 'Azure Quota API',
    timeline: 'Small increases are often auto-approved. Larger requests may require Azure support review.',
  },
};

function getDefaultJustification(q: QuotaData): string {
  const family = getFamilyDisplayName(q.provider, q.instance_family);
  const unit = q.provider === 'gcp' ? 'GPUs' : 'vCPUs';
  return `GPU compute platform requiring ${family} capacity in ${q.region} for customer workloads including ML training, inference, and quantitative data science. Current limit: ${q.quota_limit} ${unit}. Requesting increase to support production demand.`;
}

export default function QuotaIncreaseModal({ quota, groupLabel, groupFamilies, onClose, onSubmit }: Props) {
  const [requestedLimit, setRequestedLimit] = useState(quota.quota_limit * 2);
  const [justification, setJustification] = useState(getDefaultJustification(quota));
  const [submitting, setSubmitting] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    status: string;
    request_id?: number;
    provider_request_id?: string;
    provider_case_id?: string;
  } | null>(null);

  const isGroupRequest = !!groupLabel;
  const providerInfo = PROVIDER_INFO[quota.provider];
  const isLargeJump = requestedLimit > quota.quota_limit * 2;

  const handleSubmit = async () => {
    if (requestedLimit <= quota.quota_limit) {
      setError('Requested limit must be greater than current limit');
      return;
    }

    if (justification.trim().length < 10) {
      setError('Please provide a justification (at least 10 characters)');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/quotas/request-increase`,
        {
          provider: quota.provider,
          region: quota.region,
          instance_family: quota.instance_family,
          requested_limit: requestedLimit,
          justification: justification
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit quota increase request');
    } finally {
      setSubmitting(false);
    }
  };

  const suggestedLimits = [
    Math.ceil(quota.quota_limit * 1.5),
    quota.quota_limit * 2,
    quota.quota_limit * 3,
  ].filter((limit, i, arr) => limit > quota.quota_limit && arr.indexOf(limit) === i);

  // Success state
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl p-6 max-w-lg w-full mx-4 border border-gray-800">
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Request Submitted</h3>
            <p className="text-gray-400 text-sm mb-4">
              Submitted via {providerInfo?.api || `${quota.provider} API`}
            </p>

            {/* Tracking IDs */}
            <div className="text-left bg-gray-800/50 rounded-lg p-3 mb-4 space-y-2">
              {result.provider_request_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Request ID</span>
                  <span className="text-white font-mono text-xs break-all max-w-[240px] text-right">
                    {result.provider_request_id}
                  </span>
                </div>
              )}
              {result.provider_case_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Case ID</span>
                  <span className="text-white font-mono text-xs">{result.provider_case_id}</span>
                </div>
              )}
            </div>

            <p className="text-gray-500 text-xs">
              Status will be tracked automatically. Check the Requests tab for updates.
            </p>
            {providerInfo && (
              <p className="text-gray-500 text-xs mt-1">{providerInfo.timeline}</p>
            )}
          </div>
          <button
            onClick={onSubmit}
            className="w-full mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-xl p-6 max-w-lg w-full mx-4 border border-gray-800 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white">Request Quota Increase</h3>
            <p className="text-gray-400 text-sm mt-1">
              {quota.provider.toUpperCase()} &bull; {quota.region} &bull; {isGroupRequest ? groupLabel : getFamilyDisplayName(quota.provider, quota.instance_family)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Provider API Info */}
        {providerInfo && (
          <div className="mb-4 p-3 bg-blue-900/10 border border-blue-800/30 rounded-lg">
            <p className="text-blue-300 text-sm font-medium">
              Will submit via {providerInfo.api}
            </p>
            <p className="text-blue-400/70 text-xs mt-1">{providerInfo.timeline}</p>
          </div>
        )}

        {/* Provider Tips */}
        {PROVIDER_TIPS[quota.provider] && (
          <div className="mb-4">
            <button
              onClick={() => setTipsOpen(!tipsOpen)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Tips for {quota.provider.toUpperCase()} approval</span>
              <svg className={`w-3 h-3 transition-transform ${tipsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {tipsOpen && (
              <ul className="mt-2 p-3 bg-gray-800/30 border border-gray-700/50 rounded-lg space-y-1.5">
                {PROVIDER_TIPS[quota.provider].map((tip, i) => (
                  <li key={i} className="text-gray-400 text-xs flex items-start gap-2">
                    <span className="text-yellow-500/70 mt-0.5 shrink-0">&bull;</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Current Status */}
        <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 text-sm">Current Limit</p>
              <p className="text-white text-lg font-semibold">{quota.quota_limit}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Current Usage</p>
              <p className="text-white text-lg font-semibold">
                {quota.current_usage} ({Math.round(quota.utilization_percent)}%)
              </p>
            </div>
          </div>
          {quota.estimated_gpu_cards && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <p className="text-gray-400 text-sm">Estimated GPU Cards</p>
              <p className="text-blue-400 font-medium">
                ~{quota.estimated_gpu_cards.usage} / {quota.estimated_gpu_cards.limit} GPUs
                <span className="text-gray-500 text-xs ml-2">({quota.estimated_gpu_cards.raw_vcpus} vCPUs)</span>
              </p>
            </div>
          )}
        </div>

        {/* Shared Quota Info */}
        {isGroupRequest && groupFamilies && (
          <div className="mb-4 p-3 bg-blue-900/20 border border-blue-800/50 rounded-lg text-blue-300 text-sm">
            This quota is shared across all {groupLabel} families ({groupFamilies.join(', ')}).
            An increase applies to the entire group.
          </div>
        )}

        {/* Requested Limit */}
        <div className="mb-4">
          <label className="block text-gray-400 text-sm mb-2">Requested Limit</label>
          <input
            type="number"
            min={quota.quota_limit + 1}
            value={requestedLimit}
            onChange={(e) => setRequestedLimit(Number(e.target.value))}
            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
          />
          <div className="flex space-x-2 mt-2">
            {suggestedLimits.map(limit => (
              <button
                key={limit}
                onClick={() => setRequestedLimit(limit)}
                className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 text-sm rounded border border-gray-700"
              >
                {limit}
              </button>
            ))}
          </div>
        </div>

        {/* Large jump warning */}
        {isLargeJump && (
          <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-800/50 rounded-lg text-yellow-300 text-sm">
            Requesting &gt;2x current limit. Large jumps may receive additional scrutiny.
            Consider incremental increases if time permits.
          </div>
        )}

        {/* Justification */}
        <div className="mb-4">
          <label className="block text-gray-400 text-sm mb-2">Justification</label>
          <div className="flex flex-wrap gap-1 mb-2">
            {JUSTIFICATION_TEMPLATES.map((tpl) => (
              <button
                key={tpl.label}
                onClick={() => setJustification(tpl.text)}
                className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 text-xs rounded border border-gray-700"
              >
                {tpl.label}
              </button>
            ))}
          </div>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Explain why you need this quota increase..."
            rows={4}
            className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none resize-none"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-4 py-2 ${
              submitting
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-lg transition-colors flex items-center`}
          >
            {submitting && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
